import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * The replay transport: an index that advances on a timer the viewer controls.
 *
 * <h2>Why the clock is here and not on the server</h2>
 *
 * The SSE stream paces on the backend, which makes the speed unchangeable once a run has started,
 * pausing equivalent to dropping the connection, and stepping backwards impossible. Those are the
 * three controls that make a replay useful. With the whole timeline already in memory, all three
 * are just an integer moving.
 *
 * <h2>THE STALE-CLOSURE TRAP, which is the whole reason this is a hook and not four lines</h2>
 *
 * The obvious implementation is
 *
 * <pre>
 *   useEffect(() => {
 *     const id = setInterval(() => setIndex(i => i + 1), delayMs);
 *     return () => clearInterval(id);
 *   }, [delayMs, playing]);
 * </pre>
 *
 * and it has a specific, ugly failure. Every change to `delayMs` tears down the interval and
 * starts a new one, so dragging a speed slider across its range restarts the timer on every
 * animation frame of the drag — and a restart always waits a full period before its first tick,
 * so the replay STALLS for as long as the user is dragging. The requirement is explicitly that
 * speed be adjustable mid-run, so the control that adjusts it must not stop the thing it is
 * adjusting.
 *
 * So the delay is read from a REF inside a self-rescheduling `setTimeout`. Each tick schedules the
 * next one using whatever the delay is at that instant: a change takes effect on the following
 * bar, no timer is ever torn down, and dragging the slider is smooth.
 *
 * <h2>setTimeout, not requestAnimationFrame</h2>
 *
 * rAF is capped at the display refresh (~16.7ms) and, more importantly, is throttled to roughly
 * once a second in a background tab — a replay left running on another tab would silently crawl
 * and the viewer would come back to a position that means nothing. A timeout keeps its cadence.
 * Below one frame time the two are indistinguishable anyway, and `MIN_DELAY_MS` stops a zero from
 * becoming a busy loop that locks the tab.
 */

/**
 * Slowest and fastest the transport will run, in MILLISECONDS PER BAR.
 *
 * <h2>Below 1 ms/bar the timer cannot help, so the clock STRIDES instead</h2>
 *
 * `MIN_DELAY_MS` is 1 because that is the floor a `setTimeout` chain can honour — and in
 * practice not even that: browsers clamp a nested timeout to about 4 ms, so a nominal 1 ms/bar
 * really runs at roughly 250 bars a second however small the number says it is. Asking for
 * 0.2 ms/bar by setting a smaller delay would have been a label, not a speed.
 *
 * <p>So a sub-millisecond setting is delivered by advancing SEVERAL BARS PER TICK.
 * {@link resolveSpeed} turns ms/bar into the pair the clock actually runs on, and 0.2 ms/bar is
 * a 1 ms tick advancing 5 bars — genuinely five times the old ceiling rather than nominally.
 *
 * <p>`MAX_DELAY_MS` is 2000: two seconds per frame, which is the slowest the transport offers.
 */
export const MIN_DELAY_MS = 1;
export const MAX_DELAY_MS = 2000;
export const DEFAULT_DELAY_MS = 120;

/** The fastest the transport will run, in ms per bar. 5x the old 1 ms/bar ceiling. */
export const FASTEST_MS_PER_BAR = 0.2;

export const clampDelay = (ms) => Math.min(MAX_DELAY_MS,
  Math.max(MIN_DELAY_MS, Number.isFinite(ms) ? ms : DEFAULT_DELAY_MS));

/**
 * MS PER BAR -> the tick interval and the number of bars each tick advances.
 *
 * <p>At or above 1 ms/bar this is the identity it has always been: one bar per tick, the delay
 * as asked. Below it the delay pins at the 1 ms floor and the stride carries the rest, so the
 * BARS PER SECOND the setting names is what the clock delivers.
 *
 * @param msPerBar the slider's own axis, clamped to [FASTEST_MS_PER_BAR, MAX_DELAY_MS]
 */
export const boundSpeed = (msPerBar) => Math.min(MAX_DELAY_MS,
  Math.max(FASTEST_MS_PER_BAR, Number.isFinite(msPerBar) ? msPerBar : DEFAULT_DELAY_MS));

export const resolveSpeed = (msPerBar) => {
  const bounded = boundSpeed(msPerBar);
  if (bounded >= MIN_DELAY_MS) {
    return { delayMs: bounded, stride: 1 };
  }
  // ROUNDED, and floored at 2: a stride of 1 here would silently deliver 1 ms/bar under a
  // label promising less, which is the fiction this whole mechanism exists to avoid.
  return { delayMs: MIN_DELAY_MS, stride: Math.max(2, Math.round(MIN_DELAY_MS / bounded)) };
};

/**
 * @param total       how many frames there are
 * @param initialDelay starting speed, in ms per bar
 * @returns {{index, playing, delayMs, play, pause, toggle, step, back, seek, restart, atEnd}}
 */
/**
 * @param breakpoints frame indices to stop ON while playing. A Set, or null/empty for none.
 *
 *   BREAKPOINTS ARE A DEBUGGER'S, not a playlist's: the clock stops when it ARRIVES at one and
 *   does not stop again if you press play while sitting on it. Without that second half the play
 *   button is dead on every trade entry, which is exactly where somebody wants to resume from.
 *
 *   THE SIMULATOR PASSES NONE. A session carries 142 of them on SPY, so an armed set turned play
 *   into a hundred and forty-two presses; it marks those frames instead — see `markers`.
 *
 * @param markers frame indices that are INTERESTING but are not brakes. `atBreakpoint` reports
 *                them so a caller can label the bar it is sitting on; the clock never stops for
 *                one. A caller that wants both passes the same Set twice, which is the honest
 *                way to say "stop here AND label it".
 */
export default function useReplayClock(total, initialDelay = DEFAULT_DELAY_MS, breakpoints = null,
  markers = null) {
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  // THE SLIDER'S OWN AXIS, ms per bar, which may be sub-millisecond. `delayMs` stays the
  // name every caller already reads; what changed is that below 1 it is delivered by the
  // stride rather than by the timer.
  const [delayMs, setDelayMsState] = useState(boundSpeed(initialDelay));

  // Read by the running timer. A ref rather than a dependency, so changing the speed does not
  // restart the timer — see the note above.
  const delayRef = useRef(resolveSpeed(initialDelay).delayMs);
  /** Bars advanced per tick. 1 at every speed of 1 ms/bar or slower — see resolveSpeed. */
  const strideRef = useRef(resolveSpeed(initialDelay).stride);
  const totalRef = useRef(total);
  /*
   * The index, readable from a callback without making it a dependency.
   *
   * <p>`play` has to know whether the clock is at the end, and naming `index` in its dependency
   * array would give the transport a new callback on every bar -- which is a fresh prop for the
   * play button on every tick of a 8,466-bar replay. A ref beside the three already here.
   */
  const indexRef = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => { totalRef.current = total; }, [total]);
  useEffect(() => { indexRef.current = index; }, [index]);

  // Read through a ref for the same reason the delay is: the tick loop must see the current set
  // without being torn down and rebuilt, which would restart the timer and drop a pending bar.
  const breakpointsRef = useRef(breakpoints);
  useEffect(() => { breakpointsRef.current = breakpoints; }, [breakpoints]);

  /**
   * A HARD STOP for auto-play: the clock halts ON this frame and clears the target.
   *
   * <p>Separate from breakpoints because it means something different. A breakpoint is a standing
   * place the clock always stops at; this is a one-shot destination set when auto-play starts, and
   * it is what makes "run this trade and stop at its exit" expressible at all. Without it,
   * auto-play would have to be stopped by a watcher outside the clock — which reads the index one
   * render LATE and so overshoots the exit bar by one, putting the panel on a bar the trade no
   * longer existed for.
   */
  const stopAtRef = useRef(null);
  const [stopAt, setStopAtState] = useState(null);
  const setStopAt = useCallback((frame) => {
    const target = frame === null || frame === undefined ? null : Math.trunc(frame);
    stopAtRef.current = target;
    setStopAtState(target);
  }, []);

  const setDelayMs = useCallback((ms) => {
    const bounded = boundSpeed(ms);
    const { delayMs: tick, stride } = resolveSpeed(bounded);
    delayRef.current = tick;
    strideRef.current = stride;
    // THE ASKED-FOR SPEED, not the resolved tick. A reader who picks 0.2 ms/bar must see
    // 0.2 -- showing the 1 ms floor would report a speed the clock is not running at.
    setDelayMsState(bounded);
  }, []);

  const clear = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // A new timeline resets the transport. Without this, loading a second symbol leaves the
  // playhead at a frame index from the previous run — pointing at a bar that no longer exists.
  useEffect(() => {
    setIndex(0);
    setPlaying(false);
  }, [total]);

  useEffect(() => {
    if (!playing) {
      clear();
      return undefined;
    }
    let cancelled = false;

    const tick = () => {
      if (cancelled) return;
      setIndex((current) => {
        const last = Math.max(0, totalRef.current - 1);
        if (current >= last) {
          // Stop AT the last frame rather than wrapping or running past it. A replay that looped
          // would show the book resetting to zero, which reads as a bug in the statistics.
          setPlaying(false);
          return last;
        }
        // STRIDE, clamped at the last frame. Below 1 ms/bar the timer is at its floor and
        // the stride is what makes the setting mean anything -- see resolveSpeed.
        const next = Math.min(last, current + Math.max(1, strideRef.current));
        // STOP ON the breakpoint, not before it. Pausing at current would leave the bar that
        // opened the trade unrendered, so the HUD would be describing the bar before the one
        // being asked about — the off-by-one this whole feature exists to eliminate.
        if (breakpointsRef.current && breakpointsRef.current.has(next)) {
          setPlaying(false);
        }
        // The one-shot destination, checked INSIDE the same updater so the stop lands on the
        // exact frame rather than one render later. Cleared on arrival: a target that persisted
        // would re-pause the next time playback passed through it.
        if (stopAtRef.current !== null && next >= stopAtRef.current) {
          // LAND ON IT, never past it. With a stride greater than one an un-clamped `next` can
          // jump over the destination, and Next Trade would stop on a bar after the trade it
          // was asked to stop at -- the off-by-one the breakpoint note above exists to kill.
          const landed = Math.min(next, stopAtRef.current);
          setPlaying(false);
          stopAtRef.current = null;
          setStopAtState(null);
          return landed;
        }
        return next;
      });
      if (!cancelled) {
        timerRef.current = setTimeout(tick, delayRef.current);
      }
    };

    timerRef.current = setTimeout(tick, delayRef.current);
    return () => { cancelled = true; clear(); };
  }, [playing, clear]);

  useEffect(() => clear, [clear]);

  const atEnd = total > 0 && index >= total - 1;

  const play = useCallback(() => {
    /*
     * PRESSING PLAY AT THE END DOES NOTHING, and that is a reversal.
     *
     * <p>It used to restart from bar 0, on the reasoning that a dead button is the commonest
     * small annoyance in this kind of control. The cost of that convenience is the whole point
     * of reaching the end: the ledger, the statistics and the final canvas are the OUTPUT of
     * the run, and one click on the most obvious button in the transport silently replaced them
     * with an empty book. A reader reviewing a finished simulation could destroy it by pressing
     * the thing that looks like "resume".
     *
     * <p>Restarting is still one click away and always was — the transport has its own Restart
     * button beside this one — so the convenience was redundant as well as destructive.
     */
    if (totalRef.current > 0 && indexRef.current >= totalRef.current - 1) return;
    setPlaying(true);
  }, []);

  const pause = useCallback(() => {
    setPlaying(false);
    // A manual pause abandons the destination. Resuming afterwards should play normally rather
    // than silently re-arming a stop the viewer has already interrupted.
    stopAtRef.current = null;
    setStopAtState(null);
  }, []);

  /**
   * Play from here and stop ON `frame`.
   *
   * <p>`delay` is applied for the duration, so auto-play can run at one bar a second while the
   * ordinary transport keeps whatever speed the slider is set to. The previous speed is NOT
   * restored afterwards: a control that silently changed the slider back would make the slider
   * lie about what the clock is doing.
   */
  const playTo = useCallback((frame, delay) => {
    if (frame === null || frame === undefined) return;
    if (delay !== undefined && delay !== null) {
      delayRef.current = clampDelay(delay);
      setDelayMsState(clampDelay(delay));
    }
    stopAtRef.current = Math.trunc(frame);
    setStopAtState(Math.trunc(frame));
    setPlaying(true);
  }, []);
  const toggle = useCallback(() => (playing ? pause() : play()), [playing, pause, play]);

  const seek = useCallback((next) => {
    setIndex(Math.max(0, Math.min(Math.trunc(next) || 0, Math.max(0, totalRef.current - 1))));
  }, []);

  // Stepping PAUSES. Nudging one bar while the clock keeps running means the bar you asked to
  // look at is gone before you have read it.
  //
  // Both use the functional form rather than reading `index` from the closure: with the clock
  // running, `index` in this render is already stale by the time a click lands, so `index + 1`
  // would jump backwards to wherever the last render happened to be.
  const step = useCallback(() => {
    setPlaying(false);
    setIndex((i) => Math.min(i + 1, Math.max(0, totalRef.current - 1)));
  }, []);

  const back = useCallback(() => {
    setPlaying(false);
    setIndex((i) => Math.max(0, i - 1));
  }, []);

  /**
   * Go straight to a frame and STOP there.
   *
   * <p>Distinct from {@link seek}, which moves the playhead and leaves the clock running — right
   * for dragging a slider, wrong for landing on something you want to look at. A jump that kept
   * playing would carry you off the bar you asked for before you had read it, which is the same
   * reason `step` pauses.
   *
   * <p>One call rather than pause-then-seek at the call site, so there is no ordering to get
   * wrong and no window in which a pending tick can fire between the two.
   */
  const jump = useCallback((next) => {
    setPlaying(false);
    setIndex(Math.max(0, Math.min(Math.trunc(next) || 0, Math.max(0, totalRef.current - 1))));
  }, []);

  const restart = useCallback(() => { setPlaying(false); setIndex(0); }, []);

  // The chip, from the markers when a caller supplied them and from the brakes otherwise: a
  // caller that arms breakpoints has already said those frames are worth naming.
  const labelled = markers || breakpoints;
  const atBreakpoint = !!(labelled && labelled.has(index));

  return {
    index, playing, delayMs, stride: strideRef.current, atEnd, total, atBreakpoint,
    stopAt,
    play, pause, toggle, step, back, seek, jump, restart, setDelayMs, playTo, setStopAt,
  };
}
